import { Markdown } from "components/Form/components/Markdown/Markdown";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import { asyncActions } from "containers/Resources/components/CommonActions/CommonActions";
import {
    ListActionResult,
    WrappedResource,
} from "containers/Resources/components/CommonActions/interfaces";
import { checkContainerItemType } from "utils/utils";

export const getContainerVerb = (verb: string) => {
    switch (verb) {
        case asyncActions.restore:
            return "Restore";
        case asyncActions.moveToTrash:
            return "Move to trash";
        case asyncActions.permanentlyDelete:
            return "Permanent deletion";
        default:
            return verb;
    }
};

export const createNotification = (
    verb: string,
    resourceList: WrappedResource[],
    resultList: ListActionResult[],
    isExecutionAsyncAction?: boolean
) => {
    const successful: ListActionResult[] = [];
    const failed: ListActionResult[] = [];

    resourceList.forEach((res) => {
        let hit = false;
        resultList.forEach((r) => {
            if (r.resource.resourceData.id === res.resourceData.id) {
                hit = true;
                r.success ? successful.push(r) : failed.push(r);
            }
        });

        if (!hit) {
            // if the resource has already been filtered by the frontend, we can assume it's state
            // is already equal to the target state
            failed.push({
                resource: res,
                success: false,
                error: "resource is probably already in the desired state",
            });
        }
    });
    const isAsyncAction = Object.values(asyncActions).includes(verb);
    const containersSuccessful = isAsyncAction
        ? successful.reduce(
              (acc: number, rec: any) => acc + +checkContainerItemType(rec.resource.resourceName),
              0
          )
        : 0;

    const customObjectsAsyncActionSuccessful = isAsyncAction
        ? successful.reduce(
              (acc: number, rec: any) =>
                  acc +
                  +(
                      rec.resource.resourceName === "custom_object" ||
                      rec.resource.resourceName === "custom_object"
                  ),
              0
          )
        : 0;

    const restSuccessful =
        successful.length - containersSuccessful - customObjectsAsyncActionSuccessful;

    const containersFailed = isAsyncAction
        ? failed.reduce(
              (acc: number, rec: any) => acc + +checkContainerItemType(rec.resource.resourceName),
              0
          )
        : 0;

    const customObjectsAsyncActionFailed = isAsyncAction
        ? failed.reduce(
              (acc: number, rec: any) =>
                  acc +
                  +(
                      rec.resource.resourceName === "custom_object" ||
                      rec.resource.resourceName === "custom_object"
                  ),
              0
          )
        : 0;

    const restFailed = failed.length - containersFailed - customObjectsAsyncActionFailed;

    const getExecutionAsyncActionSuccess = (verb: string) => {
        const action = getContainerVerb(verb);
        return `${action} of ${
            restSuccessful
                ? `${restSuccessful}
                ${restSuccessful > 1 ? "objects" : "object"} successfully started.
                `
                : ""
        }`;
    };

    const notificationSuccess = isExecutionAsyncAction
        ? getExecutionAsyncActionSuccess(verb)
        : `
        ${
            containersSuccessful
                ? `${getContainerVerb(verb)} of ${containersSuccessful} ${
                      containersSuccessful > 1 ? "projects and bundles" : "project or bundle"
                  } and all ${
                      containersSuccessful > 1 ? "their" : "its"
                  } associated resources successfully started. 
                `
                : ""
        }
        ${
            customObjectsAsyncActionSuccessful
                ? `${getContainerVerb(verb)} of ${customObjectsAsyncActionSuccessful} ${
                      customObjectsAsyncActionSuccessful > 1 ? "objects" : "object"
                  } successfully started.
                `
                : ""
        }
        ${
            restSuccessful
                ? `${restSuccessful} 
                ${restSuccessful > 1 ? "objects" : "object"} ${verb} successfully. 
                `
                : ""
        }
        `;

    if (successful.length && !failed.length) {
        // all successful
        NotificationService.success(notificationSuccess);
    } else if (successful.length && failed.length) {
        // some successful, some failed
        const notificationText = `
            ${notificationSuccess}
             ${
                 containersFailed
                     ? `${containersFailed} ${
                           containersFailed > 1 ? "projects and bundles" : "project or bundle"
                       } and all ${
                           containersFailed > 1 ? "their" : "its"
                       } associated resources could not be ${verb}. 
                    `
                     : ""
             }
            ${
                customObjectsAsyncActionFailed
                    ? `${getContainerVerb(verb)} of ${customObjectsAsyncActionFailed} 
                    ${
                        customObjectsAsyncActionFailed > 1 ? "objects" : "object"
                    } could not be started successfully.
                    `
                    : ""
            }
            ${
                restFailed
                    ? `${restFailed} 
                    ${restFailed > 1 ? "objects" : "object"} could not be ${verb}.
                    `
                    : ""
            }
        `;

        NotificationService.warn(
            <ExpandableNotification
                title={notificationText}
                details={
                    <ul>
                        {failed.map(({ resource, error }) => (
                            <li key={resource.resourceData.id}>
                                {resource.resourceData.name ?? resource.resourceData.id}:{" "}
                                {
                                    <Markdown
                                        markdownString={error.message ?? error}
                                        className="notificationMarkdown"
                                    />
                                }
                            </li>
                        ))}
                    </ul>
                }
                detailsForClipboard={JSON.stringify(
                    failed.map(({ error, resource }: any) => ({
                        [resource.resourceData.name ?? resource.resourceData.id]:
                            error.message ?? error,
                    }))
                )}
            />
        );
    } else if (!successful.length && failed.length) {
        // all failed
        const notificationText = `
             ${
                 containersFailed
                     ? `${containersFailed} ${
                           containersFailed > 1 ? "projects and bundles" : "project or bundle"
                       } and all ${
                           containersFailed > 1 ? "their" : "its"
                       } associated resources could not be ${verb}. 
                    `
                     : ""
             }
            ${
                restFailed
                    ? `${restFailed} 
                    ${restFailed > 1 ? "objects" : "object"} could not be ${verb}.
                    `
                    : ""
            }
        `;

        NotificationService.error(
            <ExpandableNotification
                title={notificationText}
                details={
                    <ul>
                        {failed.map(({ resource, error }) => (
                            <li key={resource.resourceData.id}>
                                {resource.resourceData.name ?? resource.resourceData.id}:{" "}
                                {
                                    <Markdown
                                        markdownString={error.message ?? error}
                                        className="notificationMarkdown"
                                    />
                                }
                            </li>
                        ))}
                    </ul>
                }
                detailsForClipboard={JSON.stringify(
                    failed.map(({ error, resource }: any) => ({
                        [resource.resourceData.name ?? resource.resourceData.id]:
                            error.message ?? error,
                    }))
                )}
            />
        );
    }
};
